import { Box, Button, Chip, Link, Stack, TextField, Typography } from '@mui/material';
import type { DragEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextEditor } from '../richText';
import type { DetailsFile, DetailsLink, DetailsPatch, DetailsValues } from '../state';
import { formatFileSize } from '../state/detailsView';

import { FIELD_HEIGHT_SX, FormField } from './FormField';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * What it's about, edited (brief §3): the description as rich text (decision
 * 3, ADR 0052), a drop zone for files, and "+ Add link".
 *
 * Files are sample-only (§3, §6): a dropped or browsed file adds its name and
 * size to the card, nothing is uploaded or stored, and there is nothing to
 * open. Links are real addresses and open in a new tab from the view.
 */
export type AboutFormProps = {
  draft: Pick<DetailsValues, 'description' | 'files' | 'links'>;
  onChange: (patch: DetailsPatch) => void;
  /** Opened from the empty state's Attach file: go straight to the file picker. */
  browseOnOpen?: boolean;
};

/** A chip's ×, named for a screen reader ("Remove ATR.pdf"). */
const RemoveIcon = ({ label, ...props }: { label: string; className?: string }) => (
  <Box component="span" role="button" aria-label={label} {...props} sx={{ display: 'inline-flex' }}>
    <MaterialSymbol name="close" size={16} />
  </Box>
);

let nextId = 0;
const newId = (prefix: string) => {
  nextId += 1;
  return `${prefix}${String(Date.now())}-${String(nextId)}`;
};

const withScheme = (raw: string): string => {
  const url = raw.trim();
  return /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
};

export const AboutForm = ({ draft, onChange, browseOnOpen = false }: AboutFormProps) => {
  const { t } = useTranslation('pmDetails');
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [linkForm, setLinkForm] = useState<{ url: string; name: string } | null>(null);

  // The empty state's Attach file is still the user's click, so the browser
  // lets the picker open.
  useEffect(() => {
    if (browseOnOpen) fileInput.current?.click();
  }, [browseOnOpen]);

  const addFiles = (list: FileList | null) => {
    const added: DetailsFile[] = Array.from(list ?? []).map((file) => ({
      id: newId('f'),
      name: file.name,
      sizeBytes: file.size,
    }));
    if (added.length > 0) onChange({ files: [...draft.files, ...added] });
  };

  const addLink = () => {
    if (!linkForm || linkForm.url.trim() === '') return;
    const url = withScheme(linkForm.url);
    const link: DetailsLink = {
      id: newId('l'),
      name: linkForm.name.trim() || url.replace(/^https?:\/\//, ''),
      url,
    };
    onChange({ links: [...draft.links, link] });
    setLinkForm(null);
  };

  /** Esc in the link form closes it, not the card; Enter adds the link. */
  const linkKeys = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setLinkForm(null);
    } else if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      addLink();
    }
  };

  return (
    <Stack sx={{ gap: 2 }}>
      <FormField label={t('about.Description')} htmlFor="details-description">
        <RichTextEditor
          id="details-description"
          value={draft.description}
          onChange={(description) => onChange({ description })}
          label={t('about.Description')}
          autoFocus={!browseOnOpen}
        />
      </FormField>

      <FormField label={t('about.Attachments')}>
        <Stack
          direction="row"
          data-automation-id="details-about-drop"
          onDragOver={(event: DragEvent) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event: DragEvent) => {
            event.preventDefault();
            setDragOver(false);
            addFiles(event.dataTransfer.files);
          }}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 2,
            border: 'var(--border-width-thin) dashed',
            borderColor: dragOver ? 'var(--color-blue-80)' : 'var(--color-fog-60)',
            bgcolor: dragOver ? 'var(--color-blue-10)' : 'var(--color-fog-10)',
            borderRadius: 'var(--border-radius-medium)',
            color: 'var(--color-fog-80)',
          }}
        >
          <MaterialSymbol name="upload_file" size={20} />
          <Typography variant="bodyRegular" component="span" sx={{ color: 'inherit' }}>
            {t('about.DropFiles')}{' '}
            <Link
              component="button"
              type="button"
              onClick={() => fileInput.current?.click()}
              data-automation-id="details-about-browse"
              sx={{ verticalAlign: 'baseline', font: 'inherit' }}
            >
              {t('about.Browse')}
            </Link>
          </Typography>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = '';
            }}
            data-automation-id="details-about-file-input"
          />
        </Stack>
      </FormField>

      {draft.files.length + draft.links.length > 0 ? (
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          {draft.files.map((file) => (
            <Chip
              key={file.id}
              color="fog"
              icon={<MaterialSymbol name="picture_as_pdf" size={18} />}
              label={`${file.name} · ${formatFileSize(file.sizeBytes)}`}
              onDelete={() => onChange({ files: draft.files.filter((f) => f.id !== file.id) })}
              deleteIcon={<RemoveIcon label={t('about.Remove', { name: file.name })} />}
              data-automation-id="details-about-draft-file"
            />
          ))}
          {draft.links.map((link) => (
            <Chip
              key={link.id}
              color="fog"
              icon={<MaterialSymbol name="link" size={18} />}
              label={link.name}
              onDelete={() => onChange({ links: draft.links.filter((l) => l.id !== link.id) })}
              deleteIcon={<RemoveIcon label={t('about.Remove', { name: link.name })} />}
              data-automation-id="details-about-draft-link"
            />
          ))}
        </Stack>
      ) : null}

      {linkForm ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2,
            alignItems: 'end',
          }}
        >
          <FormField label={t('about.LinkUrl')} htmlFor="details-link-url">
            <TextField
              id="details-link-url"
              autoFocus
              size="small"
              sx={FIELD_HEIGHT_SX}
              value={linkForm.url}
              placeholder="https://"
              onChange={(event) => setLinkForm({ ...linkForm, url: event.target.value })}
              onKeyDown={linkKeys}
              slotProps={{ htmlInput: { 'data-automation-id': 'details-link-url' } }}
            />
          </FormField>
          <FormField label={t('about.LinkName')} htmlFor="details-link-name">
            <TextField
              id="details-link-name"
              size="small"
              sx={FIELD_HEIGHT_SX}
              value={linkForm.name}
              placeholder={t('about.LinkNamePlaceholder')}
              onChange={(event) => setLinkForm({ ...linkForm, name: event.target.value })}
              onKeyDown={linkKeys}
              slotProps={{ htmlInput: { 'data-automation-id': 'details-link-name' } }}
            />
          </FormField>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: 'center', height: 'var(--input-height-medium)' }}
          >
            <Button variant="text" color="secondary" onClick={() => setLinkForm(null)}>
              {t('card.Cancel')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={addLink}
              data-automation-id="details-link-add"
            >
              {t('about.AddLink')}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box>
          <Button
            variant="text"
            color="secondary"
            startIcon={<MaterialSymbol name="add" size={18} />}
            onClick={() => setLinkForm({ url: '', name: '' })}
            data-automation-id="details-about-add-link"
          >
            {t('about.AddLink')}
          </Button>
        </Box>
      )}
    </Stack>
  );
};
